/**
 * Review Reports Page
 * AIDLC Administrative Backend
 *
 * Persisted view of Bill's code reviews and Asha's QA test reports.
 * Shows stats bar, filter tabs, report list with cards, and a detail
 * view with the full markdown report and findings table.
 *
 * Personas: Executive, Ops, Orchestrator, Gatekeeper
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtDateTime } from '../../utils/formatters';
import type {
  ReviewReport,
  ReviewReportStats,
  ReviewFinding,
  FindingSeverity,
} from '../../services/orchestrationService';
import {
  getReviewReports,
  getReviewReportStats,
} from '../../services/orchestrationService';

// ── Helpers ─────────────────────────────────────────────────────────

/** Rating badge for Bill's -2 to +2 scale */
function RatingBadge({ rating }: { rating: number }) {
  const label = rating >= 0 ? `+${rating}` : `${rating}`;
  let variant: 'success' | 'info' | 'neutral' | 'warning' | 'danger' = 'neutral';
  if (rating === 2) variant = 'success';
  else if (rating === 1) variant = 'info';
  else if (rating === 0) variant = 'neutral';
  else if (rating === -1) variant = 'warning';
  else if (rating === -2) variant = 'danger';
  return <Badge variant={variant}>{label}</Badge>;
}

/** QA status badge for Asha */
function QAStatusBadge({ status }: { status: string }) {
  let variant: 'success' | 'warning' | 'danger' = 'success';
  let label = status.toUpperCase();
  if (status === 'pass') { variant = 'success'; label = 'PASS'; }
  else if (status === 'needs_fixes') { variant = 'warning'; label = 'NEEDS FIXES'; }
  else if (status === 'fail') { variant = 'danger'; label = 'FAIL'; }
  return <Badge variant={variant}>{label}</Badge>;
}

/** Severity badge for findings */
function SeverityBadge({ severity }: { severity: FindingSeverity }) {
  const config: Record<FindingSeverity, { variant: 'danger' | 'warning' | 'info' | 'neutral'; label: string }> = {
    critical: { variant: 'danger', label: 'Critical' },
    high: { variant: 'danger', label: 'High' },
    medium: { variant: 'warning', label: 'Medium' },
    low: { variant: 'info', label: 'Low' },
  };
  const { variant, label } = config[severity] || config.medium;
  return <Badge variant={variant}>{label}</Badge>;
}

/** Finding status badge */
function FindingStatusBadge({ status }: { status: string }) {
  const config: Record<string, { variant: 'success' | 'warning' | 'neutral' | 'info'; label: string }> = {
    open: { variant: 'warning', label: 'Open' },
    fixed: { variant: 'success', label: 'Fixed' },
    wont_fix: { variant: 'neutral', label: "Won't Fix" },
    deferred: { variant: 'info', label: 'Deferred' },
  };
  const { variant, label } = config[status] || config.open;
  return <Badge variant={variant}>{label}</Badge>;
}

/** Escape HTML entities to prevent XSS */
function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Pre-process plain-text reports into structured markdown.
 * Runtime reports from Bill/Asha agents often arrive as single-line text with no
 * newlines or markdown formatting. This function detects common patterns (bug IDs,
 * verdicts, section headers, comma-separated lists) and converts them to markdown
 * so the downstream renderMarkdown() can produce readable HTML.
 *
 * If the text already contains markdown formatting (headers, double-newlines),
 * it is returned as-is.
 */
function preprocessReportText(text: string): string {
  if (!text) return '';

  // Already has markdown formatting (headers + paragraph breaks)? Leave it alone.
  const hasHeaders = /^#{1,3}\s/m.test(text);
  if (hasHeaders) return text;

  let result = text;

  // Known finding-ID prefixes used by Bill/Asha agents
  const ID_PREFIXES = '(?:BUG|CR|QA|SEC|PERF|HIGH|MEDIUM|LOW|HI|ME|LO|B)';

  // ── Step 1: Insert newlines before recognizable patterns ─────────
  // This breaks the wall-of-text into sentences we can parse.

  // Bug/finding IDs: BUG-001, CR-001, QA-001, HIGH-002, etc.
  result = result.replace(new RegExp(`\\.\\s*(${ID_PREFIXES}-\\d+)`, 'g'), '.\n$1');

  // Section keywords that start new sections
  result = result.replace(
    /\.\s*(Initial:|After fixes?:|Final verdict:|Overall:|Verdict:|Summary:|Scope:|Security:|Clean Code|Findings:|Files reviewed:|Test[s]? (?:Passed|Executed|Run)|Bugs? Found:|Recommendation:|Assessment:)/gi,
    '.\n\n$1'
  );

  // "X user flows tested:" or "X tests passed:" etc.
  result = result.replace(
    /\.\s*(\d+\s+(?:user flows?|tests?|endpoints?|files?|pages?|components?|routes?)\s+\w+:)/gi,
    '.\n\n$1'
  );

  // ── Step 2: Detect and format report header ──────────────────────
  // "Asha Patel QA Report." or "Bill Torres Code Review +2." at the start
  result = result.replace(
    /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(QA Report|QA Test Report|Code Review|Code Review Report)\s*([+-]\d)?\.?\s*/i,
    (_, name, type, rating) => {
      let header = `# ${type}\n**Reviewer:** ${name.trim()}`;
      if (rating) header += ` | **Rating: ${rating}**`;
      return header + '\n\n';
    }
  );

  // ── Step 3: Format verdict sections ──────────────────────────────
  result = result.replace(
    /Initial:\s*(PASS|FAIL|NEEDS FIXES|NEEDS_FIXES)\s*(\([^)]*\))?\.?\s*/gi,
    (_, verdict, detail) => `## Initial Verdict: ${verdict.toUpperCase()}\n${detail ? detail + '\n' : ''}\n`
  );
  result = result.replace(
    /After\s+fixes?:\s*(PASS|FAIL|NEEDS FIXES|NEEDS_FIXES)\.?\s*/gi,
    (_, verdict) => `## After Fixes: ${verdict.toUpperCase()}\n\n`
  );
  result = result.replace(
    /(?:Final\s+)?[Vv]erdict:\s*([^\n]{1,200})\.?\s*$/gm,
    '## Verdict\n$1\n'
  );

  // ── Step 4: Format bug/finding entries as list items ─────────────
  // Pattern: "BUG-001 HIGH: description (FIXED: resolution)."
  // Uses constrained captures to prevent ReDoS: [^(.\n]* instead of .*?
  const findingRe = new RegExp(
    `(${ID_PREFIXES}-\\d+)\\s+(CRITICAL|HIGH|MEDIUM|LOW):\\s*([^(.\\n]*)(?:\\s*\\(FIXED:\\s*([^)]+)\\))?\\.?\\s*(?=\\n|$|${ID_PREFIXES}-\\d+)`,
    'gi'
  );
  result = result.replace(findingRe, (_, id, severity, desc, fix) => {
    let line = `- **${id.toUpperCase()}** [${severity.toUpperCase()}]: ${desc.trim()}`;
    if (fix) line += ` *(Fixed: ${fix.trim()})*`;
    return line + '\n';
  });

  // ── Step 5: Format "X user flows tested: a, b, c, ..." ──────────
  result = result.replace(
    /(\d+\s+(?:user flows?|tests?|endpoints?)\s+\w+):\s*((?:[^.\n]+,\s*){1,}[^.\n]+)\.?/gi,
    (_, header, items) => {
      const list = items.split(',').map((i: string) => `- ${i.trim()}`).join('\n');
      return `### ${header.trim()}\n${list}\n`;
    }
  );

  // ── Step 6: Format key-value summaries ───────────────────────────
  // "Tests Passed: 12/12" or "Bugs Found: 2 (1 fixed, 1 deferred)"
  result = result.replace(
    /(?:^|\n)((?:Tests? Passed|Tests? Executed|Bugs? Found|Buttons\s+\w+\s+Tested|Files Reviewed|Coverage|Total Findings|Lines Changed|Recommendation)\s*:\s*[^\n]+)/gi,
    '\n- $1'
  );

  // ── Step 7: Clean up ────────────────────────────────────────────
  // Remove lines that are just a period (protects .env, .NET references)
  result = result.replace(/^\.\s*$/gm, '');
  // Collapse 3+ consecutive newlines to 2
  result = result.replace(/\n{3,}/g, '\n\n');
  // Trim
  result = result.trim();

  return result;
}

/** Simple markdown-to-html renderer for the full report view.
 *  Security: All text content is HTML-escaped before being inserted into HTML tags.
 *  The escaping happens per-content-segment (not globally), so markdown syntax
 *  characters are processed first, then the text within each element is escaped. */
function renderMarkdown(md: string): string {
  // Step 1: Extract fenced code blocks and replace with placeholders (protect from other transforms)
  const codeBlocks: string[] = [];
  let processed = md.replace(/```[\s\S]*?```/g, (match) => {
    const code = match.replace(/```\w*\n?/, '').replace(/\n?```$/, '');
    const idx = codeBlocks.length;
    codeBlocks.push(`<pre style="background:#f3f4f6;padding:12px;border-radius:6px;overflow-x:auto;font-size:12px;line-height:1.5;margin:8px 0;"><code>${escapeHtml(code)}</code></pre>`);
    return `\x00CODEBLOCK_${idx}\x00`;
  });

  // Step 2: Escape all remaining HTML entities in the text
  processed = escapeHtml(processed);

  // Step 3: Apply markdown transforms on the escaped text
  let html = processed
    // Inline code
    .replace(/`([^`]+)`/g, '<code style="background:#f3f4f6;padding:2px 6px;border-radius:3px;font-size:12px;">$1</code>')
    // Headers (content is already escaped)
    .replace(/^### (.+)$/gm, '<h4 style="font-size:14px;font-weight:600;margin:16px 0 8px;color:#1a1a2e;">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="font-size:15px;font-weight:600;margin:20px 0 8px;color:#1a1a2e;">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="font-size:17px;font-weight:700;margin:20px 0 10px;color:#1a1a2e;">$1</h2>')
    // Bold (content is already escaped)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Tables (cell content is already escaped)
    .replace(/^\|(.+)\|$/gm, (match) => {
      const cells = match.split('|').filter(c => c.trim() !== '');
      if (cells.every(c => /^[\s-:]+$/.test(c))) return '';
      const tds = cells.map(c => `<td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:12px;">${c.trim()}</td>`).join('');
      return `<tr>${tds}</tr>`;
    })
    // Unordered lists (content is already escaped)
    .replace(/^- (.+)$/gm, '<li style="margin:2px 0;padding-left:4px;">$1</li>')
    // Newlines to breaks
    .replace(/\n/g, '<br/>');

  // Wrap consecutive <li> elements in <ul>
  html = html.replace(/(<li[^>]*>.*?<\/li>(<br\/>)?)+/g, (match) => {
    const cleaned = match.replace(/<br\/>/g, '');
    return `<ul style="list-style:disc;padding-left:20px;margin:8px 0;">${cleaned}</ul>`;
  });

  // Wrap consecutive <tr> elements in <table>
  html = html.replace(/(<tr>.*?<\/tr>(<br\/>)?)+/g, (match) => {
    const cleaned = match.replace(/<br\/>/g, '');
    return `<table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:6px;margin:8px 0;">${cleaned}</table>`;
  });

  // Clean up excessive <br/> tags
  html = html.replace(/(<br\/>){3,}/g, '<br/><br/>');

  // Step 4: Restore code blocks from placeholders
  html = html.replace(/\x00CODEBLOCK_(\d+)\x00/g, (_, idx) => codeBlocks[parseInt(idx)]);

  return html;
}

type FilterTab = 'all' | 'code_review' | 'qa_test';

// ── Main Component ──────────────────────────────────────────────────

export function ReviewReportsPage() {
  const [reports, setReports] = useState<ReviewReport[]>([]);
  const [stats, setStats] = useState<ReviewReportStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [selectedReport, setSelectedReport] = useState<ReviewReport | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [reportsData, statsData] = await Promise.all([
        getReviewReports(),
        getReviewReportStats(),
      ]);
      setReports(reportsData);
      setStats(statsData);
    } catch (err) {
      console.error('[ReviewReports] Failed to load:', err);
      setError('Failed to load review reports. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredReports =
    activeTab === 'all' ? reports : reports.filter((r) => r.type === activeTab);

  const tabCounts = {
    all: reports.length,
    code_review: reports.filter((r) => r.type === 'code_review').length,
    qa_test: reports.filter((r) => r.type === 'qa_test').length,
  };

  // Severity breakdown for a report's findings
  const findingSeverityCounts = (findings: ReviewFinding[]) => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const f of findings) {
      if (counts[f.severity] !== undefined) counts[f.severity]++;
    }
    return counts;
  };

  // ── Detail View ─────────────────────────────────────────────────

  if (selectedReport) {
    const severities = findingSeverityCounts(selectedReport.findings);
    return (
      <div className="space-y-4">
        {/* Back button */}
        <button
          onClick={() => setSelectedReport(null)}
          className="flex items-center gap-1.5 text-sm text-ldaja-blue hover:underline"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M15 19l-7-7 7-7" />
          </svg>
          Back to Reports
        </button>

        {/* Report Header */}
        <Card>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={selectedReport.type === 'code_review' ? 'purple' : 'info'}>
                  {selectedReport.type === 'code_review' ? 'Code Review' : 'QA Test'}
                </Badge>
                {selectedReport.rating !== null && selectedReport.rating !== undefined && (
                  <RatingBadge rating={selectedReport.rating} />
                )}
                {selectedReport.qaStatus && (
                  <QAStatusBadge status={selectedReport.qaStatus} />
                )}
                <span className="text-xs text-gray-400">{selectedReport.id}</span>
              </div>
              <h2 className="text-lg font-semibold text-ldaja-black">{selectedReport.subject}</h2>
              <p className="text-sm text-gray-500 mt-1">
                Reviewed by <span className="font-medium text-ldaja-black">{selectedReport.reviewerName}</span>
                {' '}on {fmtDateTime(selectedReport.createdAt)}
              </p>
              {selectedReport.backlogItemId && (
                <p className="text-xs text-gray-400 mt-1">
                  Linked to backlog item: <span className="font-mono">{selectedReport.backlogItemId}</span>
                </p>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3">{selectedReport.summary}</p>
        </Card>

        {/* Findings Table */}
        {selectedReport.findings.length > 0 && (
          <Card title={`Findings (${selectedReport.findings.length})`} subtitle={`${severities.critical} critical, ${severities.high} high, ${severities.medium} medium, ${severities.low} low`}>
            <div className="overflow-x-auto -mx-4 -mb-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-2 font-medium text-gray-500">ID</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Severity</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Title</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">File</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedReport.findings.map((f) => (
                    <tr key={f.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-2.5 font-mono text-gray-600">{f.id}</td>
                      <td className="px-4 py-2.5"><SeverityBadge severity={f.severity} /></td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-ldaja-black">{f.title}</div>
                        <div className="text-gray-500 mt-0.5 max-w-md">{f.description}</div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-gray-500">
                        {f.file ? (
                          <span>
                            {f.file}
                            {f.line !== null && <span className="text-gray-400">:{f.line}</span>}
                          </span>
                        ) : (
                          <span className="text-gray-300">--</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <FindingStatusBadge status={f.status} />
                        {f.fixedInCommit && (
                          <span className="ml-1 text-[10px] text-gray-400 font-mono">{f.fixedInCommit}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Files Reviewed */}
        {selectedReport.filesReviewed.length > 0 && (
          <Card title={`Files Reviewed (${selectedReport.filesReviewed.length})`}>
            <div className="flex flex-wrap gap-1.5">
              {selectedReport.filesReviewed.map((file) => (
                <span
                  key={file}
                  className="inline-flex items-center px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs font-mono text-gray-600"
                >
                  {file}
                </span>
              ))}
            </div>
          </Card>
        )}

        {/* Full Report (Markdown) */}
        <Card title="Full Report">
          <div
            className="prose prose-sm max-w-none text-sm text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(preprocessReportText(selectedReport.fullReport)) }}
          />
        </Card>

        {/* Metadata */}
        {Object.keys(selectedReport.metadata).length > 0 && (
          <Card title="Metadata">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(selectedReport.metadata).map(([key, value]) => (
                <div key={key} className="bg-gray-50 rounded-lg px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                  </div>
                  <div className="text-sm font-semibold text-ldaja-black mt-0.5">
                    {typeof value === 'number' ? value.toLocaleString() : String(value)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  }

  // ── List View ───────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingSpinner message="Loading review reports..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">{error}</p>
        <button
          onClick={loadData}
          className="mt-2 text-xs text-red-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Total Reports</div>
            <div className="text-2xl font-bold text-ldaja-black mt-1">{stats.totalReports}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Bill Reviews</div>
            <div className="text-2xl font-bold text-ldaja-black mt-1">{stats.byReviewer.bill || 0}</div>
            {stats.latestRating !== null && (
              <div className="mt-1">
                <span className="text-[10px] text-gray-400">Latest: </span>
                <RatingBadge rating={stats.latestRating} />
              </div>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Asha Reviews</div>
            <div className="text-2xl font-bold text-ldaja-black mt-1">{stats.byReviewer.asha || 0}</div>
            {stats.latestQaStatus && (
              <div className="mt-1">
                <span className="text-[10px] text-gray-400">Latest: </span>
                <QAStatusBadge status={stats.latestQaStatus} />
              </div>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Rating Distribution</div>
            <div className="flex items-center gap-1 mt-2">
              {['+2', '+1', '0', '-1', '-2'].map((r) => (
                <div key={r} className="text-center flex-1">
                  <div className="text-xs font-bold text-ldaja-black">{stats.ratingCounts[r] || 0}</div>
                  <div className="text-[9px] text-gray-400">{r}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
        {([
          { key: 'all' as FilterTab, label: 'All', count: tabCounts.all },
          { key: 'code_review' as FilterTab, label: 'Code Reviews', count: tabCounts.code_review },
          { key: 'qa_test' as FilterTab, label: 'QA Tests', count: tabCounts.qa_test },
        ]).map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === key
                ? 'bg-ldaja-blue text-white'
                : 'text-gray-500 hover:text-ldaja-black hover:bg-gray-50'
            }`}
          >
            {label}
            <span
              className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                activeTab === key
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Report List */}
      {filteredReports.length === 0 ? (
        <EmptyState
          title="No Review Reports"
          description={
            activeTab === 'all'
              ? 'No review reports have been created yet. Bill and Asha will generate reports during the DoD pipeline.'
              : activeTab === 'code_review'
              ? 'No code reviews found. Bill generates these during Step 2 of the DoD pipeline.'
              : 'No QA test reports found. Asha generates these during Step 3 of the DoD pipeline.'
          }
        />
      ) : (
        <div className="space-y-2">
          {filteredReports.map((report) => {
            const severities = findingSeverityCounts(report.findings);
            return (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report)}
                className="w-full text-left bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-ldaja-blue/30 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Type + badges row */}
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={report.type === 'code_review' ? 'purple' : 'info'}>
                        {report.type === 'code_review' ? 'Code Review' : 'QA Test'}
                      </Badge>
                      {report.rating !== null && report.rating !== undefined && (
                        <RatingBadge rating={report.rating} />
                      )}
                      {report.qaStatus && (
                        <QAStatusBadge status={report.qaStatus} />
                      )}
                    </div>

                    {/* Subject */}
                    <h3 className="text-sm font-semibold text-ldaja-black group-hover:text-ldaja-blue transition-colors truncate">
                      {report.subject}
                    </h3>

                    {/* Reviewer + date */}
                    <p className="text-xs text-gray-500 mt-0.5">
                      {report.reviewerName} &middot; {fmtDateTime(report.createdAt)}
                    </p>
                  </div>

                  {/* Right side: findings + files */}
                  <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                    {/* Findings breakdown */}
                    {report.findings.length > 0 && (
                      <div className="text-right">
                        <div className="text-xs font-medium text-ldaja-black">
                          {report.findings.length} finding{report.findings.length !== 1 ? 's' : ''}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 justify-end">
                          {severities.critical > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                              {severities.critical}C
                            </span>
                          )}
                          {severities.high > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                              {severities.high}H
                            </span>
                          )}
                          {severities.medium > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-600 font-medium">
                              {severities.medium}M
                            </span>
                          )}
                          {severities.low > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">
                              {severities.low}L
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Files count */}
                    <div className="text-right">
                      <div className="text-xs font-medium text-ldaja-black">
                        {report.filesReviewed.length}
                      </div>
                      <div className="text-[10px] text-gray-400">files</div>
                    </div>

                    {/* Arrow */}
                    <svg className="w-4 h-4 text-gray-300 group-hover:text-ldaja-blue transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
